import {
  createDynamicCityData,
  getAllCities,
  getCanonicalCitySlug,
  getCityData,
} from "../config/cityData";
import { CityData } from "../types/city.types";
import { GET_ALL_CITIES, GET_AREAS_BY_CITY } from "./queries";

const TURF_URL = process.env.NEXT_PUBLIC_TURF_URL ?? "";

export interface ApiCity {
  id: string;
  name: string;
  showBookNow: boolean;
}

export interface ApiArea {
  id: string;
  name: string;
  cityId: string;
}

export interface ResolvedApiCity {
  apiCity: ApiCity;
  cityData: CityData;
}

interface GraphQLResponse<T> {
  data?: T;
  errors?: Array<{ message?: string }>;
}

const resolveCityRecord = (city: ApiCity): CityData => {
  return (
    getCityData(city.name) ||
    createDynamicCityData({
      id: city.id,
      name: city.name,
      showBookNow: city.showBookNow,
    })
  );
};

export const mergeCitiesBySlug = (...citySets: CityData[][]): CityData[] => {
  const cityMap = new Map<string, CityData>();

  citySets.flat().forEach((city) => {
    if (!city?.slug || cityMap.has(city.slug)) return;
    cityMap.set(city.slug, city);
  });

  return Array.from(cityMap.values());
};

export const fetchGraphQL = async <T>(
  query: string,
  variables: Record<string, unknown> = {}
): Promise<T> => {
  if (!TURF_URL) {
    throw new Error("TURF_URL is not defined");
  }

  const response = await fetch(`${TURF_URL}/graphql`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ query, variables }),
  });

  if (!response.ok) {
    throw new Error(`GraphQL API responded with status: ${response.status}`);
  }

  const result: GraphQLResponse<T> = await response.json();
  if (result.errors?.length) {
    const message = result.errors
      .map((error) => error.message)
      .filter(Boolean)
      .join(", ");

    throw new Error(message || "GraphQL API returned errors");
  }

  return (result.data ?? {}) as T;
};

export const fetchApiCities = async (): Promise<ApiCity[]> => {
  const data = await fetchGraphQL<{ cities?: ApiCity[] }>(GET_ALL_CITIES);
  return data.cities || [];
};

export const fetchResolvedApiCities = async (): Promise<CityData[]> => {
  const apiCities = await fetchApiCities();
  return mergeCitiesBySlug(apiCities.map(resolveCityRecord));
};

export const fetchResolvedApiCityCatalog = async (): Promise<ResolvedApiCity[]> => {
  const apiCities = await fetchApiCities();
  const seenSlugs = new Set<string>();

  return apiCities.reduce<ResolvedApiCity[]>((catalog, apiCity) => {
    const cityData = resolveCityRecord(apiCity);
    if (seenSlugs.has(cityData.slug)) {
      return catalog;
    }

    seenSlugs.add(cityData.slug);
    catalog.push({ apiCity, cityData });
    return catalog;
  }, []);
};

export const fetchCityCatalog = async (): Promise<CityData[]> => {
  try {
    const apiCities = await fetchResolvedApiCities();
    return mergeCitiesBySlug(apiCities, getAllCities());
  } catch {
    return getAllCities();
  }
};

export const resolveApiCityData = async (
  requestedCity: string,
  apiCities?: ApiCity[]
): Promise<CityData | null> => {
  const cities = apiCities || (await fetchApiCities());
  const requestedSlug = getCanonicalCitySlug(requestedCity);
  const matchedCity = cities.find(
    (city) => getCanonicalCitySlug(city.name) === requestedSlug
  );

  if (!matchedCity) return null;
  return resolveCityRecord(matchedCity);
};

export const fetchApiAreasByCity = async (cityId: string): Promise<ApiArea[]> => {
  const data = await fetchGraphQL<{ areasByCity?: ApiArea[] }>(
    GET_AREAS_BY_CITY,
    { cityId }
  );

  return data.areasByCity || [];
};
